import {_, Postmate, React, observer, observable, computed, action, useLocalStore, classNames, Iframe, useAsObservableSource, autorun, runInAction, useEffect, useRef, toJS} from '@api'
import {TabNode} from '@flexlayout'
import {IFlexLayoutTabProps} from '@stores'
import {authStore} from '@utils'
import {trace} from 'mobx'
import * as css from './C8FlexLayoutTab.css'
import {c8RawCss, IC8TabConfig, IC8TabConfigSettings, IFlexLayoutCatalogRootConfig, IFlexLayoutIFrameTabConfig, IFrameFlexLayoutTabProps, IframeFlexLayoutTabStoreContext, IFrameWithTitleBar} from '.'
import c8OverrideCss from '!!raw-loader!./C8Overrides.css'

/// region Store

export class C8FrameFlexLayoutTabStore {
  constructor(public props: Partial<IFrameFlexLayoutTabProps>) {
    this.tab.iframe = this
  }

  @observable.ref postmate: Postmate.ParentAPI
  @observable.ref ref: HTMLDivElement

  registerEffects = () => {
    const toDispose = [
      autorun(async () => {
        const {tab, ref, url, props: {flexNode, flexNode: {id}}} = this
        if (this.ref) {
          this.initIframe()
        }
      }),

      autorun(() => {
        this.autorun_redirectToCorrectPage()
      }, {name: `Update C8 internal URL as needed`})
    ]

    return () => {
      try {
        console.log(`Destroying postmate (unmount)`)
        this.postmate?.destroy()
      } catch (err) {}
      toDispose.forEach(d => d())
    }
  }

  @computed get parentTab() {
    const {settings: {parentTabId}, tab: {model}} = this
    return parentTabId ? model.nodeById<TabNode<IFlexLayoutCatalogRootConfig>>(parentTabId) : null
  }

  /**
   * Of the form: centric://APPAREL/Style/1
   * @param centricUrl
   */
  @action navigateToC8Url = (centricUrl: string) => {
    if (this.postmate && centricUrl != this.settings.frameCentricUrl) {
      DEBUG && console.debug(`Navigating c8 frame ${this.tab.displayName} - ${this.tab.id} to ${centricUrl}`)
      this.settings.frameCentricUrl = centricUrl
      this.postmate.call('navigateToUrl', centricUrl)
    }
  }

  @computed get injectCss() {
    const {settings} = this

    //console.log(c8RawCss)
    return `
      ${settings?.injectCss ?? ''}
      ${c8OverrideCss}
      ${settings?.subComponent ? `
      ${c8RawCss.hideGuiElements}
      ` : ''}
    `
  }

  @observable mustHaveAtLeastOneObservable = true

  get tab(): TabNode<IC8TabConfig> { return this.props.flexNode as TabNode<IC8TabConfig>}

  @computed get settings(): IC8TabConfigSettings { return this.tab.config?.settings ?? {} }

  @computed get url() {
    const {settings, tab: {flexLayout: {stores: {users: {user}, config: {config: {centricURL}}}}}} = this
    // return `https://www.google.com/webhp?igu=1`
    // return `http://cvip-c8.centricsoftware.com/WebAccess/login.html`
    const baseUrl = `${_.trimEnd(centricURL, '/')}/WebAccess/home.html#SessionURL=${authStore.user?.sessionUrl}` // This wont' work currently and will cause an infinite redirect loop

    console.log(baseUrl)

    return baseUrl

    // if (!this.initialized) return baseUrl
    //
    // if (settings?.queryString) {
    //   return `${baseUrl}${settings.queryString}`
    // }
    //
    // return baseUrl
  }

  @computed get config(): Partial<IC8TabConfig> {
    return this.tab.config
    //return _.merge({settings: { token: 'centric://_CS_SecurityToken/dc737f23-de7a-4339-8044-5fd1758c7372'}}, this.tab.config)
  }

  @action injectCssStylesIntoIframe = () => {
    const {injectCss, postmate} = this
    console.debug(`Injecting CSS styles into C8 frame - ${this.tab.displayName} - ${this.tab.id}`, {injectCss})
    postmate.call('injectCssStyles', injectCss)
  }

  @observable initialized = false

  autorun_redirectToCorrectPage = () => {
    const {postmate, navigateToC8Url, settings: {subComponent}, parentTab} = this
    //if (!postmate) throw new Error(`Cannot redirect C8 if postmate is not established`)

    // console.log({subComponent, parentTab})
    if (postmate && parentTab) {
      switch (subComponent) {
        case 'collection-targets':
          const {catalogRoot: {vlp: {hierarchy: {selected, leafSelections}}}} = parentTab
          console.log(toJS({leafSelections, selected}, {recurseEverything: true}))
          const tail = _.last(leafSelections)
          // trace()
          if (tail) {
            this.navigateToC8Url(tail.id)
          }
          break
        default:
          break
      }
    }
  }

  postmateTimeout: number

  @action initIframe = async () => {
    const {tab, tab: {id}, ref, url} = this
    try {
      if (this.postmate) {
        console.debug(`Restarting postmate in tab ${id}`)

        this.postmate?.destroy()
      } else {
        console.debug(`Initializing postmate in tab ${id}`)
      }
    } catch (err) {}

    ref.innerHTML = ''
    //Postmate.debug = true
    // this.postmateTimeout = setTimeout(() => {
    //   console.debug(`Timing out wating to connect to C8, restarting...`)
    //   this.initIframe()
    // }, 15000)

    this.postmate = await new Postmate({
      container: ref, // Element to inject frame into
      url: url, // Page to load, must have postmate.js. This will also be the origin used for communication.
      name: id, // Set Iframe name attribute. Useful to get `window.name` in the child.
      classListArray: [css.iframe] //Classes to add to the iframe via classList, useful for styling.
    })

    clearTimeout(this.postmateTimeout)

    runInAction(() => {
      console.debug(`Postmate initialized ${id} - ${tab.displayName}`)
      this.injectCssStylesIntoIframe()
      this.initialized = true
    })

    this.postmate.on('C8-navigate', (frameCentricUrl) => {
      console.debug(`C8 navigated to ${frameCentricUrl}`)
      this.settings.frameCentricUrl = frameCentricUrl
      //this.injectCssStylesIntoIframe()
    })
  }
}

export const C8FlexLayoutTabStoreContext = React.createContext<C8FrameFlexLayoutTabStore>(null)
export const useC8FlexLayoutTabStore = () => React.useContext(C8FlexLayoutTabStoreContext)

/// endregion

/// region Components

interface MyProps extends IFlexLayoutTabProps {
  className?: string
}

export const C8FlexLayoutTab = observer<MyProps>(function C8FlexLayoutTab({className, flexNode, ...props}) {
  const obsProps = useAsObservableSource({flexNode}),
    store = useLocalStore(() => new C8FrameFlexLayoutTabStore(obsProps)),
    ref = useRef<HTMLDivElement>(null)

  // console.log(props)
  useEffect(() => {
    store.ref = ref.current
  }, [ref.current])

  useEffect(() => store.registerEffects(), [])

  return (
    <C8FlexLayoutTabStoreContext.Provider value={store}>
      <div className={classNames(css.root, className)} data-loaded={store.initialized || true}>
        {/*<LoadingIndicator/>*/}
        <div className={classNames(css.inject)} ref={ref}>
        </div>
      </div>
    </C8FlexLayoutTabStoreContext.Provider>)
})

/// endregion



// WEBPACK FOOTER //
// ./src/stores/catalog/flexLayout/flexComponents/content-panels/C8FlexLayoutTab.tsx